import { defineStore } from 'pinia';
import { useCmsProxy, useNuxtApp } from '#imports';
import { useModalStore } from '~dsc/stores/modal';
import { useCheckoutStore } from '~dsc/stores/checkout';
import { useUserStore } from '~dsc/stores/cms/user';

export const useCartStore = defineStore('cart', {
    state: () => ({
        cart: [],
        totals: {
            discount: 0,
            taxAmount: 0,
            totalWithTax: 0,
            totalWithoutTax: 0,
        },
        cartAmounts: 0,
        discountCode: '',
    }),
    actions: {
        deleteProduct(code) {
            this.cart = this.cart.filter(product => product.code !== code);
            this.setTotals();
            this.saveToLocal();
        },
        reset() {
            this.cart = [];
            this.setTotals();
            this.saveToLocal();
        },
        addToCart(product, silent) {
            let add = false;
            if (this.cart?.length){
                this.cart.forEach(cartProduct => {
                    if (cartProduct?.code === product.code) {
                        cartProduct.amount = cartProduct.amount + 1;

                        add = true;
                    }
                });
                if (!silent) useNuxtApp().$dsc.msg.success('addToCartNotification.addedToCart');
            } else if (!silent) useModalStore().setNameAndOpen('firstInCart');

            if (!add) this.cart.push(product);
            this.setTotals();
            this.saveToLocal();
        },
        async setTotals() {
            if (!this.cart?.length) {
                this.cartAmounts = 0;
                this.totals = {
                    discount: 0,
                    taxAmount: 0,
                    totalWithTax: 0,
                    totalWithoutTax: 0,
                };

                return;
            }

            try {
                const items = this.cart.map(item => ({
                    quantity: item.amount ? parseInt(item.amount) : 1,
                    code: item.code,
                    showUpsell: item.showUpsell ?? false,
                }));

                const userEmail = useUserStore().loggedIn ? useUserStore().user.email : useCheckoutStore().customerData.email;

                const res = await useCmsProxy('digital-order/v1/cart/validate', {
                    method: 'post',
                    body: {
                        items,
                        ...(this.discountCode && { 'discountCode': this.discountCode }),
                        ...(userEmail.length && { 'customerEmail': userEmail }),
                    },
                });

                res.items.forEach(item => {
                    try {
                        const currentItem = this.cart.find(cartItem => cartItem.code === item.product?.code);
                        let omitUpsell = false;

                        if (currentItem.includes?.length === 1
                            && item.upsellProducts?.length === 1
                            && item.upsellProducts[0].product?.bundleMembers?.length === 1
                        ) {
                            const sku = [...currentItem.includes.map(inc => inc.sku)];
                            const upsellSku = item.upsellProducts.map(upsell => upsell.product.bundleMembers.map(member => member.product.code));
                            const currentAmount = currentItem.amount * currentItem.includes[0].amount;
                            const upgradeAmount = item.upsellProducts[0].quantity * item.upsellProducts[0].product.bundleMembers[0].amount;

                            if (JSON.stringify(sku) === JSON.stringify(upsellSku)
                                && currentAmount > (Number(upgradeAmount) + 1)) {
                                omitUpsell = true;
                            }
                        }

                        if (omitUpsell) {
                            this.cart[this.cart.indexOf(currentItem)].upsells = [];
                        } else {
                            this.cart[this.cart.indexOf(currentItem)].upsells = item.upsellProducts;
                        }
                    } catch (e) {
                        if (process.env.NODE_ENV === 'development') {
                            console.error(e);
                            console.warn('Could not map upsells');
                        }
                    }
                });

                this.totals = res.totals;
                if (items.length > 0) {
                    this.cartAmounts = items.map(item => item.quantity).reduce((partialSum, a) => partialSum + a, 0);
                }
                this.saveToLocal();

                return true;
            } catch (error) {
                console.error(error);
                this.cart = [];
                this.saveToLocal();
                useNuxtApp().$dsc.msg.error('addToCartNotification.cartValidationFailed');

                return false;
            }
        },
        saveToLocal() {
            localStorage.setItem('cart', JSON.stringify(this.cart));
        },
        async loadFromLocal() {
            if (!window || !window.localStorage.getItem('cart')) return;
            this.cart = JSON.parse(window.localStorage.getItem('cart'));
            await this.setTotals();
        },
        setDiscountCode(code) {
            this.discountCode = code;
            this.setTotals();
            this.saveToLocal();
        },
        async submitOrder(type, body){
            const { $paths } = useNuxtApp();

            const res = await useCmsProxy($paths.getEndpoint(type), { method: 'POST', body });

            return res;
        },
    },
    getters: {
        getCart: state => state.cart,
        getCartAnalytics: state => state.cart.map(item => {
            const { $analytics } = useNuxtApp();

            return $analytics.productToAnalytics(item);
        }),
        getProductCount: state => state.cart.length,
        getProductItemCount: state => state.cart.length > 0 ? state.cart.map(item => item.includes.length > 0 ? item.includes.map(inc => item.amount * inc.amount).reduce((a, b) => a + b) : item.amount).reduce((a, b) => a + b) : 0,
        getTotals: state => state.totals,
        getdiscountCode: state => state.discountCode,
        getCartAmounts: state => state.cartAmounts,
        getCartProductByCode: state => code => state.cart.find(item => item.code === code),
    },
});
